import dayjs from 'dayjs';

import { TIMESERIES_TIMESTAMP_COLUMN } from '#plugin-chat/lib/data-pipeline/steps/fetch-timeseries.ts';
import type { ColumnSchema } from '#plugin-chat/lib/data-pipeline/types.ts';
import { isDatapointArraySamples } from '#plugin-chat/lib/timeseries/datapoint-detection.ts';
import { asTimestampedValue } from '#plugin-chat/lib/timeseries/datapoint-value.ts';
import {
  ColumnNotFoundError,
  DatapointArrayShapeError,
  DuplicateKeyError,
  InvalidSpecError,
  NullValueError,
} from '#plugin-chat/lib/tool/errors/index.ts';

/** Separator joining the splitBy value and the source column for N×M output names. */
export const EXPAND_TIMESERIES_COLUMN_SEPARATOR = '__';
/**
 * `operationHint` for column-not-found errors raised by `expandTimeseries`.
 * The agent's most common confusion: "I derived/joined this column right
 * before — why isn't it found?" The answer is that `expandTimeseries`
 * reshapes the table to (timestamp, expanded series), and any column not
 * named in `columns` (or used as `splitBy`) is dropped because the new
 * row grain is one row per timestamp. Two consecutive `expandTimeseries`
 * actions don't stack — the first collapses the second's source column.
 */
const RESHAPE_HINT =
  "expandTimeseries reshapes the input into a wide chart-ready table whose columns are `timestamp` plus the expanded series. Any input column not listed in `columns` (or used as `splitBy`) is dropped because the output grain is one row per timestamp. To expand multiple datapoint-array columns together, pass them all in a single `columns` array of one expandTimeseries call — chaining two expandTimeseries actions does not work because the first one drops the second one's target column.";

/** Timestamp alignment strategy across series. */
export type ExpandTimeseriesAlign = 'union' | 'inner';

/** LLM-facing parameters for the {@link expandTimeseries} helper. */
export interface ExpandTimeseriesSpec {
  /** Datapoint-array columns to expand. Each must carry arrays of `{timestamp, value|...}` objects. */
  columns: readonly string[];
  /** Optional split column — its values become per-row series labels. Required when input has >1 row. */
  splitBy?: string;
  /** Timestamp alignment across series (default: 'union'). */
  align?: ExpandTimeseriesAlign;
}

/** Output of {@link expandTimeseries}. */
export interface ExpandTimeseriesResult {
  rows: Record<string, unknown>[];
  columnSchemas: ColumnSchema[];
}

/** Per-series intermediate: the output column name and its (timestamp → value) map. */
interface SeriesEntry {
  key: string;
  timestamps: Map<string, number>;
}

/**
 * Reshape one or more datapoint-array columns into a wide-format chart-ready
 * dataset.
 *
 * Input shapes supported:
 * - **1×M** (one row, M datapoint-array columns): output columns reuse the
 *   input column names (e.g. `oilDaily` / `gasDaily` / `waterDaily`).
 *   `splitBy` is unnecessary here and ignored if absent.
 * - **N×1** (N rows, one datapoint-array column, `splitBy` required): output
 *   columns are the unique splitBy values (e.g. each well's id).
 * - **N×M** (N rows, M datapoint-array columns, `splitBy` required): output
 *   columns are compound names `<splitBy>__<column>` (e.g.
 *   `SE0070-S__weeklyMin`). Order: every (row, column) pair, row-major.
 *
 * Output shape: rows with a `timestamp` column (ISO 8601 strings) plus one
 * numeric column per series. Missing values for a `(timestamp, series)` pair
 * become `null` (Recharts renders these as line gaps).
 *
 * Timestamp alignment:
 * - `'union'` (default): the row set is the sorted union of all series'
 *   timestamps; series missing a timestamp contribute `null` for that row.
 * - `'inner'`: only timestamps present in every series are emitted.
 *
 * Throws:
 * - {@link ColumnNotFoundError} when an entry in `columns` or `splitBy` is
 *   not present on the input.
 * - {@link DatapointArrayShapeError} when a `columns` entry exists but its
 *   cells are not datapoint arrays.
 * - {@link InvalidSpecError} when input has multiple rows but no `splitBy`.
 * - {@link NullValueError} / {@link DuplicateKeyError} when `splitBy` values
 *   are not unique and non-null across rows.
 */
export function expandTimeseries(
  rows: readonly Record<string, unknown>[],
  inputColumnNames: readonly string[],
  spec: ExpandTimeseriesSpec,
): ExpandTimeseriesResult {
  const { columns, splitBy, align = 'union' } = spec;
  const knownColumns = new Set(inputColumnNames);

  const missing = columns.filter((c) => !knownColumns.has(c));
  if (missing.length > 0) {
    throw new ColumnNotFoundError('expandTimeseries', missing, inputColumnNames, RESHAPE_HINT);
  }

  if (splitBy !== undefined && !knownColumns.has(splitBy)) {
    throw new ColumnNotFoundError('expandTimeseries (splitBy)', [splitBy], inputColumnNames, RESHAPE_HINT);
  }

  if (splitBy === undefined && rows.length > 1) {
    throw new InvalidSpecError(
      `expandTimeseries: input has ${rows.length} rows but no splitBy specified — series cannot be disambiguated. Call this tool again and remember to provide splitBy parameter to disambiguate the series.`,
      {
        suggestions: [
          'Add `splitBy: "<id-column>"` so each row becomes its own series (label = the splitBy value).',
          // 'Or filter the input down to a single row first via `slice` / `filter`.',
        ],
        details: { rowCount: rows.length },
      },
    );
  }

  const nonDatapointCol = columns.find((c) => !isDatapointArraySamples(rows.map((row) => row[c])));
  if (nonDatapointCol !== undefined) {
    throw new DatapointArrayShapeError('expandTimeseries', nonDatapointCol, false);
  }

  const splitValues = resolveSplitValues(rows, splitBy);

  // Build per-series records: one entry per (row, target column). Row-major
  // order means the output column ordering reflects rows then columns.
  const seriesEntries: readonly SeriesEntry[] = rows.flatMap((row, rowIdx) =>
    columns.map((col) => ({
      key: makeSeriesKey(splitValues[rowIdx], col, columns.length),
      timestamps: extractCellTimestamps(row[col]),
    })),
  );

  const seriesOrder = seriesEntries.map((e) => e.key);
  const seriesMap = new Map(seriesEntries.map((e) => [e.key, e.timestamps] as const));

  const allTimestamps = new Set(seriesEntries.flatMap((e) => [...e.timestamps.keys()]));
  const resolvedTimestamps =
    align === 'inner'
      ? [...allTimestamps].filter((t) => seriesEntries.every((e) => e.timestamps.has(t))).sort()
      : [...allTimestamps].sort();

  const outputRows: Record<string, unknown>[] = resolvedTimestamps.map((ts) => ({
    [TIMESERIES_TIMESTAMP_COLUMN]: ts,
    ...Object.fromEntries(seriesOrder.map((key) => [key, seriesMap.get(key)?.get(ts) ?? null])),
  }));

  const columnSchemas: ColumnSchema[] = [
    { name: TIMESERIES_TIMESTAMP_COLUMN, type: 'datetime' },
    ...seriesOrder.map((name): ColumnSchema => ({ name, type: 'number' })),
  ];

  return { rows: outputRows, columnSchemas };
}

/**
 * Resolve the per-row splitBy values, validating non-null and uniqueness.
 * Returns `undefined` for every row when no splitBy was supplied.
 *
 * Throws {@link NullValueError} when any splitBy value is null/undefined,
 * or {@link DuplicateKeyError} when splitBy values are not unique across rows.
 *
 * Dedup uses a structural key (`JSON.stringify` for objects, `String` for
 * primitives) — `String(obj)` collapses every object to `[object Object]`,
 * which would both falsely flag two distinct ref-shaped values as
 * duplicates AND render uselessly in the error. The error itself receives
 * the original raw values so TOON encoding can render them properly.
 */
function resolveSplitValues(
  rows: readonly Record<string, unknown>[],
  splitBy: string | undefined,
): readonly (string | undefined)[] {
  if (splitBy === undefined) {
    return rows.map(() => undefined);
  }

  const rawValues = rows.map((row) => row[splitBy]);
  const nullCount = rawValues.filter((v) => v === null || v === undefined).length;
  if (nullCount > 0) {
    throw new NullValueError(splitBy, nullCount);
  }

  const keys = rawValues.map(toStableKey);
  const seenKeys = new Set<string>();
  const reportedDuplicates = new Set<string>();
  const duplicateRawValues: unknown[] = [];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i] ?? '';
    if (seenKeys.has(key)) {
      if (!reportedDuplicates.has(key)) {
        reportedDuplicates.add(key);
        duplicateRawValues.push(rawValues[i]);
      }
    } else {
      seenKeys.add(key);
    }
  }
  if (duplicateRawValues.length > 0) {
    throw new DuplicateKeyError(splitBy, duplicateRawValues);
  }
  return keys;
}

/**
 * Stable string key for dedup. Primitives stringify naturally; objects
 * are JSON-encoded so different objects don't collide on `String(obj)`'s
 * `[object Object]` and identical objects produce the same key.
 */
function toStableKey(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value);
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/**
 * Compose the output series-column name. Naming rules:
 * - no splitBy → use the source column name as-is (1×M case)
 * - splitBy + single source column → use the splitBy value alone (N×1 case)
 * - splitBy + multiple source columns → `<splitBy>__<column>` (N×M case)
 */
function makeSeriesKey(splitValue: string | undefined, column: string, columnCount: number): string {
  if (splitValue === undefined) {
    return column;
  }
  if (columnCount === 1) {
    return splitValue;
  }
  return `${splitValue}${EXPAND_TIMESERIES_COLUMN_SEPARATOR}${column}`;
}

/**
 * Extract the (timestamp, value) pairs from one datapoint-array cell.
 * Non-array inputs become an empty map; unparseable datapoints are filtered
 * out — caller has already validated the column-level shape via
 * {@link isDatapointArraySamples}.
 */
function extractCellTimestamps(cell: unknown): Map<string, number> {
  if (!Array.isArray(cell)) {
    return new Map();
  }
  return new Map(
    cell
      .map((dp) => asTimestampedValue(dp))
      .filter((tv): tv is { ts: Date; value: number } => tv !== null)
      .map((tv) => [dayjs(tv.ts).toISOString(), tv.value] as const),
  );
}
